import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiEnvelope } from './payment';

/**
 * Hospicash welcome-letter company config (HospicashSettingArogya). One active config drives the
 * partner welcome letter's letterhead/contacts. The three image fields hold UploadFiles GUIDs.
 * Reads are normalized defensively (PascalCase → camelCase).
 */

export interface HospicashSetting {
  id: number;
  companyName: string;
  companyShortName?: string | null;
  addressLine?: string | null;
  customerCare?: string | null;
  email?: string | null;
  website?: string | null;
  hospicashClaimNumber?: string | null;
  docAssistanceNumber?: string | null;
  letterheadImage?: string | null;
  signatureImage?: string | null;
  stampImage?: string | null;
  isActive: boolean;
  createdOn?: string | null;
}
export interface HospicashSettingDto {
  id: number; // 0 => insert, >0 => update
  companyName: string; // required
  companyShortName?: string | null;
  addressLine?: string | null;
  customerCare?: string | null;
  email?: string | null;
  website?: string | null;
  hospicashClaimNumber?: string | null;
  docAssistanceNumber?: string | null;
  letterheadImage?: string | null;
  signatureImage?: string | null;
  stampImage?: string | null;
  isActive: boolean;
}

function pick<T = any>(x: any, ...keys: string[]): T | undefined {
  for (const k of keys) if (x?.[k] != null) return x[k] as T;
  return undefined;
}
function asNum(v: any, dflt = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : dflt;
}
function asBool(v: any, dflt = false): boolean {
  if (v == null) return dflt;
  return v === true || v === 'true' || v === 1 || v === '1';
}
function asStr(v: any): string | null {
  return v == null ? null : String(v);
}

function mapRow(x: any): HospicashSetting {
  return {
    id: asNum(pick(x, 'Id', 'id')),
    companyName: pick(x, 'CompanyName', 'companyName') ?? '',
    companyShortName: asStr(pick(x, 'CompanyShortName', 'companyShortName')),
    addressLine: asStr(pick(x, 'AddressLine', 'addressLine')),
    customerCare: asStr(pick(x, 'CustomerCare', 'customerCare')),
    email: asStr(pick(x, 'Email', 'email')),
    website: asStr(pick(x, 'Website', 'website')),
    hospicashClaimNumber: asStr(pick(x, 'HospicashClaimNumber', 'hospicashClaimNumber')),
    docAssistanceNumber: asStr(pick(x, 'DocAssistanceNumber', 'docAssistanceNumber')),
    letterheadImage: asStr(pick(x, 'LetterheadImage', 'letterheadImage')),
    signatureImage: asStr(pick(x, 'SignatureImage', 'signatureImage')),
    stampImage: asStr(pick(x, 'StampImage', 'stampImage')),
    isActive: asBool(pick(x, 'IsActive', 'isActive'), true),
    createdOn: asStr(pick(x, 'CreatedOn', 'createdOn')),
  };
}

function mapList(res: any): ApiEnvelope<HospicashSetting[]> {
  return res && Array.isArray(res.data) ? { ...res, data: res.data.map(mapRow) } : { ...res, data: [] };
}
function mapObj(res: any): ApiEnvelope<HospicashSetting> {
  return res?.data && typeof res.data === 'object' ? { ...res, data: mapRow(res.data) } : res;
}

@Injectable({ providedIn: 'root' })
export class HospicashSettingService {
  private http = inject(HttpClient);
  private readonly api = environment.apiUrl;

  all() {
    return this.http.get<ApiEnvelope<any[]>>(`${this.api}/HospicashSetting/GetAll`).pipe(map(mapList));
  }
  get(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/HospicashSetting/GetById`, { params: { id } }).pipe(map(mapObj));
  }
  insert(m: HospicashSettingDto) {
    return this.http.post<ApiEnvelope<any>>(`${this.api}/HospicashSetting/Insert`, m);
  }
  update(m: HospicashSettingDto) {
    return this.http.post<ApiEnvelope<any>>(`${this.api}/HospicashSetting/Update`, m);
  }
  enable(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/HospicashSetting/Enable`, { params: { id } });
  }
  disable(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/HospicashSetting/Disable`, { params: { id } });
  }
  delete(id: number) {
    return this.http.get<ApiEnvelope<any>>(`${this.api}/HospicashSetting/Delete`, { params: { id } });
  }
}
